import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import Header from "./Header";
const UNIT_OPTIONS = ["inches", "feet", "cm", "meters"];
const TILE_COLORS  = ["#f0e8dc", "#e8ddd0", "#ede3d8", "#e4d8cc"];
const CUT_COLOR    = "#f4a56a";
const GROUT_COLOR  = "#9a8878";
const MIN_VISIBLE  = 0.25;

function toInches(v, unit) {
  if (!isFinite(v) || v <= 0) return 0;
  switch (unit) {
    case "feet":   return v * 12;
    case "cm":     return v / 2.54;
    case "meters": return v * 39.3701;
    default:       return v;
  }
}

function fmt(inches) {
  if (!isFinite(inches) || inches < 0) return '0"';
  const n32 = Math.round(inches * 32);
  const whole = Math.floor(n32 / 32), num = n32 % 32;
  if (num === 0) return `${whole}″`;
  function gcd(a, b) { return b === 0 ? a : gcd(b, a % b); }
  const g = gcd(num, 32);
  return whole === 0 ? `${num/g}/${32/g}″` : `${whole} ${num/g}/${32/g}″`;
}
function pct(v, total) { return Math.min(100, v / total * 100).toFixed(1); }

function parseFraction(s) {
  if (!s || !s.trim()) return NaN;
  s = s.trim().replace(/["″]/g, '').trim();
  const dec = parseFloat(s);
  if (!isNaN(dec) && String(dec) === s) return dec;
  const wf = s.match(/^(\d+(?:\.\d+)?)\s*[-\s]\s*(\d+)\s*\/\s*(\d+)$/);
  if (wf) { const d = parseInt(wf[3]); return d ? parseFloat(wf[1]) + parseInt(wf[2])/d : NaN; }
  const f = s.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (f) { const d = parseInt(f[2]); return d ? parseInt(f[1])/d : NaN; }
  return isNaN(dec) ? NaN : dec;
}
function isOk(v) { return isFinite(v) && v > 0; }

function solveCorners(topW, leftH, rightH, bottomW) {
  if (!isOk(topW)||!isOk(leftH)||!isOk(rightH)||!isOk(bottomW)) return null;
  const K = (bottomW*bottomW - rightH*rightH + topW*topW - leftH*leftH) / (2*topW);
  const A = leftH/topW, M = K-topW;
  const a = A*A+1, b = 2*M*A, c = M*M - rightH*rightH;
  const disc = b*b - 4*a*c;
  if (disc < 0) return null;
  const sq = Math.sqrt(disc);
  for (const s of [1,-1]) {
    const by = (-b + s*sq)/(2*a), bx = K + A*by;
    if (by > 0 && bx >= 0) {
      const d1 = Math.sqrt(bx*bx + (by-leftH)**2);
      const d2 = Math.sqrt((bx-topW)**2 + by**2);
      if (Math.abs(d1-bottomW)<0.01 && Math.abs(d2-rightH)<0.01)
        return { TL:[0,0], TR:[topW,0], BL:[0,leftH], BR:[bx,by] };
    }
  }
  return null;
}

function wallXatY(p, q, y) {
  const dy = q[1]-p[1];
  if (Math.abs(dy) < 1e-9) return null;
  const t = (y-p[1])/dy;
  if (t < -1e-6 || t > 1+1e-6) return null;
  return p[0] + t*(q[0]-p[0]);
}

function bottomWallY(x, BL, BR) {
  const dx = BR[0]-BL[0];
  if (Math.abs(dx) < 1e-9) return Math.min(BL[1], BR[1]);
  const t = Math.max(0, Math.min(1, (x-BL[0])/dx));
  return BL[1] + t*(BR[1]-BL[1]);
}

function installedDims(xGrid, yGrid, tileL, tileB, corners) {
  const { TL, TR, BR, BL } = corners;

  // Left wall: take the innermost (rightmost) x across the tile height
  const lTop = wallXatY(TL, BL, yGrid)       ?? 0;
  const lBot = wallXatY(TL, BL, yGrid+tileB) ?? 0;
  const leftBound = Math.max(lTop, lBot, 0);

  // Right wall: take the innermost (leftmost) x across the tile height
  // Used only for cut detection — rendering uses full tileL and lets clipPath clip.
  const rFallback = (y) => y > BR[1] ? BR[0] : TR[0];
  const rTop = wallXatY(TR, BR, yGrid)       ?? rFallback(yGrid);
  const rBot = wallXatY(TR, BR, yGrid+tileB) ?? rFallback(yGrid+tileB);
  const rInstalled = Math.min(xGrid + tileL, Math.min(rTop, rBot));

  const xL = Math.max(xGrid, leftBound);
  if (rInstalled - xL < 1e-6) return null; // tile fully outside room

  // iW: installed width for cut detection only
  const iW = rInstalled - xL;

  // Bottom wall: sample at tile midpoint for cut detection
  const xMid = xL + iW / 2;
  const yBotWall = bottomWallY(xMid, BL, BR);
  const yT  = Math.max(yGrid, 0);
  const yBi = Math.min(yGrid + tileB, yBotWall);
  if (yBi - yT < 1e-6) return null; // tile fully below bottom wall

  // iH: installed height for cut detection only
  const iH = yBi - yT;

  // renderW/renderH: what the tile rect should display.
  // Left/top walls are straight lines — clipPath does NOT clip interior content there.
  // So left-cut and top-cut tiles must render at their installed size (iW/iH).
  // Right-wall and bottom-wall are diagonal — clipPath handles those exactly,
  // so we render full tileL/tileB and let the polygon clip the diagonal edge.
  const renderW = (xGrid < -1e-6) ? iW : tileL;  // left cut → iW; right cut → tileL (clipped)
  const renderH = (yGrid < -1e-6) ? iH : tileB;  // top cut  → iH; bottom cut → tileB (clipped)
  return { iW, iH, renderW, renderH, xL, yT };
}

function calcLayout(topW, leftH, rightH, bottomW, tileL, tileB, gapIn, shiftL, shiftB, offset) {
  if (!isOk(topW)||!isOk(leftH)||!isOk(rightH)||!isOk(bottomW)||
      !isOk(tileL)||!isOk(tileB)||!isOk(gapIn))
    return { tiles:[], totalTiles:0, cutList:[], totalCuts:0, corners:null };

  const corners = solveCorners(topW, leftH, rightH, bottomW);
  if (!corners) return { tiles:[], totalTiles:0, cutList:[], totalCuts:0, corners:null };

  const pitchL = tileL + gapIn, pitchB = tileB + gapIn;
  const maxH = Math.max(corners.BL[1], corners.BR[1]);

  shiftL = ((shiftL % pitchL) + pitchL) % pitchL;
  shiftB = ((shiftB % pitchB) + pitchB) % pitchB;

  const x0 = shiftL > 1e-6 ? -(pitchL - shiftL) : 0;
  const y0 = shiftB > 1e-6 ? -(pitchB - shiftB) : 0;

  const tiles = [];
  const cutMap = new Map();
  function r32(v) { return Math.round(v * 32) / 32; }

  let ri = 0;
  for (let yGrid = y0; yGrid < maxH + 1e-6; yGrid += pitchB, ri++) {
    if (yGrid + tileB < 1e-6) continue;
    const rowOff = ((offset * tileL * ri) % pitchL + pitchL) % pitchL;
    const xStart = x0 - rowOff;
    let ci = 0;
    const maxW = Math.max(corners.TR[0], corners.BR[0]);
    for (let xGrid = xStart; xGrid < maxW + pitchL; xGrid += pitchL, ci++) {
      if (xGrid + tileL < 1e-6) continue;
      if (xGrid > maxW + pitchL * 2) break;

      const dims = installedDims(xGrid, yGrid, tileL, tileB, corners);
      if (!dims) continue;
      const { iW, iH, xL, yT } = dims;
      if (iW < MIN_VISIBLE || iH < MIN_VISIBLE) continue;

      const isCut = iW < tileL - 1e-6 || iH < tileB - 1e-6;
      tiles.push({ xL, yT, iW, iH, renderW: dims.renderW, renderH: dims.renderH, isCut, ri, ci });

      if (isCut) {
        const wV=r32(iW), hV=r32(iH);
        const wC=iW<tileL-1e-6, hC=iH<tileB-1e-6;
        const label = wC&&hC ? 'Corner cut' : wC ? 'Width cut' : 'Height cut';
        const key = `${wV}:${hV}:${label}`;
        const ex = cutMap.get(key);
        cutMap.set(key, ex
          ? { ...ex, count: ex.count+1 }
          : { id:key, label, w:wV, h:hV, cutsRequired:(wC?1:0)+(hC?1:0), count:1 });
      }
    }
  }

  const cutList = Array.from(cutMap.values());
  const totalCuts = cutList.reduce((s,c) => s + c.count*c.cutsRequired, 0);
  return { tiles, totalTiles: tiles.length, cutList, totalCuts, corners };
}

// FIX 1 of 2: Removed Math.max(18/tileL, ...) minimum-tile-size floor.
// The old formula forced scale >= 18/tileL, so a 400" room with 4" tiles
// produced scale=4.5, viewBox 1800px wide — overflowing the screen badly.
// Now we simply fit the room bounding box to the 520×440 preview area.
// Tiles in large rooms appear smaller, which is correct and expected.
function computeScale(topW, leftH, rightH, bottomW) {
  const corners = solveCorners(topW, leftH, rightH, bottomW);
  const maxH = corners ? Math.max(corners.BL[1], corners.BR[1]) : Math.max(leftH, rightH);
  const maxW = corners ? Math.max(corners.TR[0], corners.BR[0]) : topW;
  return Math.min(520 / maxW, 440 / maxH);
}

function TilePreview({ topW, leftH, rightH, bottomW, tileL, tileB, gapIn, offset, startMode, scale, onLayoutChange }) {
  const shiftRef = useRef({ L:0, B:0 });
  const [shiftL, setShiftL] = useState(0);
  const [shiftB, setShiftB] = useState(0);
  const [hovered, setHovered] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const drag = useRef(null);
  const svgRef = useRef(null);
  const pitchL = tileL + gapIn, pitchB = tileB + gapIn;

  function applyShift(L, B) {
    L = ((L % pitchL) + pitchL) % pitchL;
    B = ((B % pitchB) + pitchB) % pitchB;
    shiftRef.current = { L, B };
    setShiftL(L); setShiftB(B);
  }

  useEffect(() => { applyShift(0, 0); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [topW, leftH, rightH, bottomW, tileL, tileB, gapIn, offset, startMode]);

  const layout = useMemo(() => {
    const corners0 = solveCorners(topW, leftH, rightH, bottomW);
    const maxH0 = corners0 ? Math.max(corners0.BL[1], corners0.BR[1]) : Math.max(leftH, rightH);
    const wrap = (v, p) => ((v%p)+p)%p;
    let bL = 0, bB = 0;
    if      (startMode === 'right')  bL = wrap(topW, pitchL);
    else if (startMode === 'bottom') bB = wrap(maxH0, pitchB);
    else if (startMode === 'center') {
      // Place a grout gap exactly centred on topW/2.
      // Grout gap left-edge = topW/2 - gapIn/2, right-edge = topW/2 + gapIn/2.
      // shiftL = ((topW/2 + gapIn/2) % pitchL + pitchL) % pitchL
      // This places a tile right-edge at topW/2 - gapIn/2, so the grout
      // gap centre lands exactly at topW/2. Equal cuts on both sides.
      bL = ((topW / 2 + gapIn / 2) % pitchL + pitchL) % pitchL;
    }
    return calcLayout(topW, leftH, rightH, bottomW, tileL, tileB, gapIn,
      wrap(bL + shiftL, pitchL), wrap(bB + shiftB, pitchB), offset);
  }, [topW, leftH, rightH, bottomW, tileL, tileB, gapIn, shiftL, shiftB, offset, startMode]);

  useEffect(() => { onLayoutChange(layout); }, [layout, onLayoutChange]);

  const corners = layout.corners;
  const maxH = corners ? Math.max(corners.BL[1], corners.BR[1]) : Math.max(leftH, rightH);
  const maxW = corners ? Math.max(corners.TR[0], corners.BR[0]) : topW;
  const W = maxW * scale, H = maxH * scale;
  const PAD = 6;

  const polyPts = corners
    ? [corners.TL, corners.TR, corners.BR, corners.BL]
        .map(p => `${p[0]*scale},${p[1]*scale}`).join(' ')
    : `0,0 ${W},0 ${W},${rightH*scale} 0,${leftH*scale}`;

  function getSVGPoint(e) {
    if (!svgRef.current) return null;
    const pt = svgRef.current.createSVGPoint();
    pt.x = e.touches?.[0]?.clientX ?? e.clientX;
    pt.y = e.touches?.[0]?.clientY ?? e.clientY;
    return pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
  }

  function onPointerDown(e) {
    e.preventDefault();
    const sx = e.touches?.[0]?.clientX ?? e.clientX;
    const sy = e.touches?.[0]?.clientY ?? e.clientY;
    drag.current = { sx, sy, initL: shiftRef.current.L, initB: shiftRef.current.B };
    setIsDragging(true); setHovered(null);
    const move = (cx, cy) => {
      if (!drag.current) return;
      applyShift(drag.current.initL + (cx-drag.current.sx)/scale,
                 drag.current.initB + (cy-drag.current.sy)/scale);
    };
    const mm = ev => move(ev.clientX, ev.clientY);
    const tm = ev => { ev.preventDefault(); move(ev.touches[0].clientX, ev.touches[0].clientY); };
    const end = () => {
      drag.current = null; setIsDragging(false);
      window.removeEventListener('mousemove', mm);
      window.removeEventListener('mouseup', end);
      window.removeEventListener('touchmove', tm);
      window.removeEventListener('touchend', end);
    };
    window.addEventListener('mousemove', mm);
    window.addEventListener('mouseup', end);
    window.addEventListener('touchmove', tm, { passive:false });
    window.addEventListener('touchend', end);
  }

  function onSVGMouseMove(e) {
    if (isDragging) return;
    const p = getSVGPoint(e);
    if (!p) return;
    const xi = p.x/scale, yi = p.y/scale;
    const t = layout.tiles.find(t => xi>=t.xL && xi<=t.xL+(t.renderW??tileL) && yi>=t.yT && yi<=t.yT+(t.renderH??tileB));
    setHovered(t ?? null);
  }

  useEffect(() => {
    const onKey = e => {
      if (!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(e.key)) return;
      if (document.activeElement !== svgRef.current) return;
      e.preventDefault();
      const step = (e.shiftKey ? 5 : 1) / scale;
      const {L, B} = shiftRef.current;
      applyShift(
        e.key==='ArrowLeft'?L-step:e.key==='ArrowRight'?L+step:L,
        e.key==='ArrowUp'?B-step:e.key==='ArrowDown'?B+step:B);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [scale]);

  const pctH = Math.round(shiftL/pitchL*100);
  const pctV = Math.round(shiftB/pitchB*100);
  const hasShift = shiftL > gapIn || shiftB > gapIn;

  const hoverEl = useMemo(() => {
    if (!hovered || isDragging) return null;
    const t = hovered;
    // Highlight covers the installed tile area (iW×iH), not the full grid cell.
    // For edge cuts (left wall, top wall), iW/iH correctly shows the cut piece.
    // clipPath clips anything outside the room automatically.
    // Highlight uses renderW × renderH so it covers the full visible tile.
    // renderW = tileL for right-wall cuts (trapezoidal — clipPath trims the diagonal).
    // renderW = iW   for left-wall cuts (straight wall — no clipPath trimming there).
    // Same logic applies vertically for renderH vs iH.
    // The clipPath group wrapping hoverEl clips the highlight to the exact room shape.
    const x=t.xL*scale, y=t.yT*scale, w=(t.renderW??t.iW)*scale, h=(t.renderH??t.iH)*scale;
    const wC=t.iW<tileL-1e-6, hC=t.iH<tileB-1e-6;
    const lines = [
      `${fmt(t.iW)} × ${fmt(t.iH)}`,
      wC&&hC ? 'Corner — 2 cuts'
        : wC ? `Width cut — ${pct(t.iW,tileL)}% of ${fmt(tileL)}`
        : hC ? `Height cut — ${pct(t.iH,tileB)}% of ${fmt(tileB)}`
        : 'Full tile — drag to move',
    ];
    const TW=160, lH=16, TH=lines.length*lH+12, pad=8;
    let tx=x+w/2-TW/2, ty=y-TH-6;
    if(ty<pad) ty=y+h+6;
    if(tx+TW>W-pad) tx=W-TW-pad;
    tx=Math.max(pad,tx);
    return (
      <g style={{pointerEvents:'none'}}>
        <rect x={x} y={y} width={w} height={h}
          fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.8)" strokeWidth={1.5} rx={1}/>
        <rect x={tx} y={ty} width={TW} height={TH} rx={4} fill="#1a1410" opacity={0.92}/>
        {lines.map((l,i)=>(
          <text key={i} x={tx+8} y={ty+14+i*lH} fontSize={i===0?10:9}
            fontWeight={i===0?700:400}
            fill={i===0?(t.isCut?CUT_COLOR:'#f5f0e8'):'#c9b8a8'}
            fontFamily="monospace">{l}</text>
        ))}
      </g>
    );
  }, [hovered, isDragging, scale, tileL, tileB, W]);

  return (
    <div>
      <div style={{marginBottom:12,display:'flex',flexDirection:'column',gap:8}}>
        {[['Horiz',pctH,v=>applyShift(v/100*pitchL,shiftRef.current.B)],
          ['Vert', pctV,v=>applyShift(shiftRef.current.L,v/100*pitchB)]
        ].map(([lbl,val,fn])=>(
          <div key={lbl} style={{display:'flex',alignItems:'center',gap:10}}>
            <span style={S.sliderLabel}>{lbl}</span>
            <input type="range" min={0} max={100} value={val}
              onChange={e=>fn(parseFloat(e.target.value))}
              style={{flex:1,accentColor:'#c0392b',cursor:'pointer'}}/>
            <span style={S.sliderVal}>{val}%</span>
          </div>
        ))}
      </div>
      {/* FIX 2 of 2: Removed maxHeight:500.
          With width="100%" the SVG scales to fill its container width and sets height
          proportionally via the viewBox aspect ratio — so the full room is always visible.
          maxHeight:500 was fighting this: it capped the height at 500px, which then forced
          the browser to widen the SVG beyond the container to preserve the aspect ratio. */}
      <svg ref={svgRef}
        viewBox={`${-PAD} ${-PAD} ${W+PAD*2} ${H+PAD*2}`}
        width="100%" tabIndex={0}
        style={{display:'block',borderRadius:6,border:'2px solid #d4c8bc',
          boxShadow:'4px 4px 0 #d4c8bc',userSelect:'none',touchAction:'none',
          background:'#b0a090',outline:'none',
          cursor:isDragging?'grabbing':'default'}}
        onMouseMove={onSVGMouseMove}
        onMouseLeave={()=>setHovered(null)}
        onMouseDown={onPointerDown}
        onTouchStart={onPointerDown}>
        <defs>
          <clipPath id="room-clip"><polygon points={polyPts}/></clipPath>
        </defs>
        <polygon points={polyPts} fill={GROUT_COLOR}/>
        <g clipPath="url(#room-clip)">
          {layout.tiles.map((t,i)=>(
            <rect key={i}
              x={t.xL*scale} y={t.yT*scale}
              width={(t.renderW ?? tileL)*scale} height={(t.renderH ?? tileB)*scale}
              fill={t.isCut ? CUT_COLOR : TILE_COLORS[(t.ri+t.ci)%TILE_COLORS.length]}
              stroke="none"
            />
          ))}
          {hoverEl}
          {/* Centre line — room centre (topW/2), grout gap is centred exactly here */}
          {startMode === 'center' && (() => {
            // The grout gap is centred exactly at topW/2 by the shiftL formula.
            // So the CL is simply the geometric centre of the top/bottom walls.
            const clX = (topW / 2) * scale;
            const dashLen = Math.max(4, H * 0.025);
            return (
              <g style={{pointerEvents:'none'}}>
                <line x1={clX} y1={0} x2={clX} y2={H}
                  stroke="#c0392b" strokeWidth={2}
                  strokeDasharray={`${dashLen} ${dashLen * 0.6}`}
                  opacity={0.85}/>
                <rect x={clX - 14} y={4} width={28} height={16} rx={3}
                  fill="#c0392b" opacity={0.9}/>
                <text x={clX} y={16} textAnchor="middle"
                  fontSize={10} fontWeight={700} fill="#fff" fontFamily="monospace">CL</text>
              </g>
            );
          })()}
        </g>
        <polygon points={polyPts} fill="none" stroke="#c0392b" strokeWidth={2.5}/>
      </svg>
      <div style={{display:'flex',gap:14,flexWrap:'wrap',alignItems:'center',
        marginTop:10,fontSize:'0.68rem',color:'#5a4e46'}}>
        {[['#f0e8dc','Full tile'],[CUT_COLOR,'Cut tile'],[GROUT_COLOR,'Grout']].map(([bg,lbl])=>(
          <span key={lbl} style={{display:'flex',alignItems:'center',gap:5}}>
            <span style={{width:11,height:11,background:bg,border:'1px solid #d4c8bc',
              display:'inline-block',borderRadius:1}}/>
            {lbl}
          </span>
        ))}
        <span style={{color:'#7a6858'}}>Drag to pan · Sliders · Arrow keys</span>
        {hasShift&&(
          <button onClick={()=>applyShift(0,0)}
            style={{marginLeft:'auto',fontFamily:'monospace',fontSize:'0.65rem',
              background:'none',border:'1px solid #d4c8bc',borderRadius:4,
              padding:'3px 10px',cursor:'pointer',color:'#5a4e46'}}>↺ Reset</button>
        )}
      </div>
    </div>
  );
}

function StatsRow({ layout }) {
  if (!layout) return null;
  const cutTiles = layout.tiles?.filter(t=>t.isCut).length ?? 0;
  return (
    <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14,marginBottom:20}}>
      {[
        {label:'Total Tiles', value:layout.totalTiles??0, accent:true},
        {label:'Full Tiles',  value:(layout.totalTiles??0)-cutTiles},
        {label:'Cut Tiles',   value:cutTiles},
        {label:'Total Cuts',  value:layout.totalCuts??0},
      ].map(s=>(
        <div key={s.label} style={{background:s.accent?'#c0392b':'#f5f0e8',
          border:`1.5px solid ${s.accent?'#a93226':'#d4c8bc'}`,borderRadius:6,
          padding:'14px 18px',boxShadow:s.accent?'3px 3px 0 #a93226':'3px 3px 0 #d4c8bc'}}>
          <div style={{fontSize:'0.6rem',letterSpacing:'0.15em',textTransform:'uppercase',
            color:s.accent?'rgba(255,255,255,0.75)':'#5a4e46',marginBottom:4,fontFamily:'monospace'}}>{s.label}</div>
          <div style={{fontSize:'1.6rem',fontWeight:600,color:s.accent?'#fff':'#1a1410',fontFamily:'monospace',lineHeight:1.1}}>{s.value}</div>
        </div>
      ))}
    </div>
  );
}

function CutPanel({ layout, tileL, tileB }) {
  if (!layout?.cutList?.length) return (
    <div style={{...S.card,textAlign:'center',color:'#5a4e46',fontSize:'0.82rem',padding:20}}>
      {layout ? 'No cuts needed.' : 'Enter dimensions to see cuts.'}
    </div>
  );
  return (
    <div style={S.card}>
      <div style={S.cardLabel}>Cut Analysis — updates live as you drag</div>
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {layout.cutList.map(cut=>{
          const wC=cut.w<tileL-1e-6, hC=cut.h<tileB-1e-6;
          const DW=60,DH=44;
          const pw=Math.max(2,Math.min(DW,cut.w/tileL*DW));
          const ph=Math.max(2,Math.min(DH,cut.h/tileB*DH));
          return (
            <div key={cut.id} style={{display:'flex',alignItems:'flex-start',gap:14,
              background:cut.cutsRequired===2?'#fff8f2':'#fffdf8',
              border:`1.5px solid ${cut.cutsRequired===2?'#e8a87a':'#e8c89a'}`,
              borderLeft:`4px solid ${CUT_COLOR}`,borderRadius:6,padding:'12px 14px'}}>
              <svg width={DW+6} height={DH+6} style={{flexShrink:0,marginTop:2}}>
                <rect x={3} y={3} width={DW} height={DH} fill="#e8ddd0" stroke="#c9b8a8" strokeWidth={1} rx={1}/>
                <rect x={3} y={3} width={pw} height={ph} fill={CUT_COLOR} opacity={0.85} rx={1}/>
                {wC&&<line x1={3+pw} y1={3} x2={3+pw} y2={3+DH} stroke="#c0392b" strokeWidth={1.5} strokeDasharray="3 2"/>}
                {hC&&<line x1={3} y1={3+ph} x2={3+DW} y2={3+ph} stroke="#c0392b" strokeWidth={1.5} strokeDasharray="3 2"/>}
              </svg>
              <div style={{flex:1}}>
                <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap',marginBottom:4}}>
                  <span style={{fontWeight:600,fontSize:'0.8rem',color:'#1a1410'}}>{cut.label}</span>
                  <span style={{background:CUT_COLOR,color:'#fff',fontSize:'0.6rem',borderRadius:3,padding:'1px 6px'}}>× {cut.count}</span>
                  <span style={{background:cut.cutsRequired===2?'#c0392b':'#9a8878',color:'#fff',fontSize:'0.6rem',borderRadius:3,padding:'1px 6px'}}>{cut.cutsRequired} cut{cut.cutsRequired>1?'s':''}</span>
                </div>
                <div style={{fontFamily:'monospace',fontSize:'0.85rem',color:'#1a1410',fontWeight:700,marginBottom:4}}>{fmt(cut.w)} × {fmt(cut.h)}</div>
                {wC&&<div style={{fontSize:'0.67rem',color:'#5a4e46'}}>Width: <strong>{fmt(cut.w)}</strong> from <strong>{fmt(tileL)}</strong> — keep <strong>{pct(cut.w,tileL)}%</strong></div>}
                {hC&&<div style={{fontSize:'0.67rem',color:'#5a4e46'}}>Height: <strong>{fmt(cut.h)}</strong> from <strong>{fmt(tileB)}</strong> — keep <strong>{pct(cut.h,tileB)}%</strong></div>}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{marginTop:12,fontSize:'0.65rem',color:'#9a8878',fontStyle:'italic'}}>* Installed dimension — grout not included.</div>
    </div>
  );
}

function RoomDiagram({ topW, leftH, rightH, bottomW }) {
  if (!topW||!leftH||!rightH||!bottomW) return null;
  const corners = solveCorners(topW,leftH,rightH,bottomW);
  if (!corners) return (
    <div style={{marginTop:8,fontSize:'0.7rem',color:'#c0392b',textAlign:'center'}}>
      These wall lengths do not form a valid room shape.
    </div>
  );
  const {TL,TR,BR,BL}=corners;
  const maxH=Math.max(BL[1],BR[1]);
  const sc=Math.min(130/topW,90/maxH);
  const pts=[TL,TR,BR,BL].map(p=>`${p[0]*sc},${p[1]*sc}`).join(' ');
  const bmx=(BL[0]+BR[0])/2*sc, bmy=(BL[1]+BR[1])/2*sc;
  return (
    <svg width={topW*sc+60} height={maxH*sc+36} style={{display:'block',margin:'12px auto',overflow:'visible'}}>
      <polygon points={pts} fill="#f5f0e8" stroke="#c0392b" strokeWidth={1.5}/>
      <text x={TR[0]*sc/2} y={-7} textAnchor="middle" fontSize={9} fill="#5a4e46" fontFamily="monospace">{fmt(topW)} top</text>
      <text x={-4} y={BL[1]*sc/2} textAnchor="end" dominantBaseline="middle" fontSize={9} fill="#5a4e46" fontFamily="monospace">{fmt(leftH)} left</text>
      <text x={TR[0]*sc+4} y={BR[1]*sc/2} textAnchor="start" dominantBaseline="middle" fontSize={9} fill="#5a4e46" fontFamily="monospace">{fmt(rightH)} right</text>
      <text x={bmx} y={bmy+14} textAnchor="middle" fontSize={9} fill="#5a4e46" fontFamily="monospace">{fmt(bottomW)} bottom</text>
    </svg>
  );
}

function FracInput({ label, value, onChange, placeholder }) {
  const [raw, setRaw] = useState(value??'');
  const [error, setError] = useState(false);
  useEffect(()=>{ if(value===''||value===undefined) setRaw(''); },[value]);
  function handleChange(e) {
    const v=e.target.value; setRaw(v);
    if(v===''){onChange('');setError(false);return;}
    const p=parseFraction(v);
    if(isFinite(p)&&p>0){onChange(String(p));setError(false);}
    else{onChange('');setError(v.length>0);}
  }
  return (
    <div style={{display:'flex',flexDirection:'column',gap:4}}>
      <label style={S.label}>{label}</label>
      <input type="text" value={raw} onChange={handleChange} placeholder={placeholder||'e.g. 216'}
        style={{...S.input,borderColor:error?'#c0392b':'#d4c8bc',background:error?'#fff5f5':'#f5f0e8'}}/>
      {error&&<span style={{fontSize:'0.6rem',color:'#c0392b',fontFamily:'monospace'}}>Use: 12 or 5 1/16 or 3/4</span>}
    </div>
  );
}

function CardLabel({ children }) {
  return <div style={S.cardLabel}>{children}<span style={{flex:1,height:1,background:'#d4c8bc'}}/></div>;
}

const S = {
  label:      {fontSize:'0.65rem',letterSpacing:'0.12em',textTransform:'uppercase',color:'#5a4e46',fontFamily:'monospace'},
  input:      {fontFamily:'monospace',fontSize:'1rem',color:'#1a1410',background:'#f5f0e8',border:'1.5px solid #d4c8bc',borderRadius:4,padding:'9px 12px',width:'100%',outline:'none'},
  select:     {fontFamily:'monospace',fontSize:'0.85rem',color:'#1a1410',background:'#f5f0e8',border:'1.5px solid #d4c8bc',borderRadius:4,padding:'9px 12px',width:'100%',outline:'none',cursor:'pointer'},
  card:       {background:'#fffdf8',border:'1.5px solid #d4c8bc',borderRadius:8,padding:'24px',boxShadow:'4px 4px 0 #d4c8bc'},
  cardLabel:  {fontSize:'0.6rem',letterSpacing:'0.18em',textTransform:'uppercase',color:'#5a4e46',marginBottom:18,display:'flex',alignItems:'center',gap:8,fontFamily:'monospace'},
  sliderLabel:{fontSize:'0.65rem',color:'#5a4e46',fontFamily:'monospace',width:80,flexShrink:0,textTransform:'uppercase',letterSpacing:'0.08em'},
  sliderVal:  {fontSize:'0.7rem',fontFamily:'monospace',color:'#9a8878',width:36,textAlign:'right'},
};

export default function TileCalculator() {
  const [roomUnit,   setRoomUnit]   = useState('inches');
  const [tileUnit,   setTileUnit]   = useState('inches');
  const [topWall,    setTopWall]    = useState('');
  const [leftWall,   setLeftWall]   = useState('');
  const [rightWall,  setRightWall]  = useState('');
  const [bottomWall, setBottomWall] = useState('');
  const [tileL,      setTileL]      = useState('');
  const [tileB,      setTileB]      = useState('');
  const [gap,        setGap]        = useState('1/16');
  const [offset,     setOffset]     = useState(0);
  const [startMode,  setStartMode]  = useState('left');
  const [layout,     setLayout]     = useState(null);
  const [optimise,   setOptimise]   = useState(null); // null | 'cuts' | 'tiles'
  const [isCustom,   setIsCustom]   = useState(false); // true when user overrides best
  const onLayoutChange = useCallback(l=>setLayout(l),[]);

  const calc = useMemo(()=>{
    const tw=parseFraction(topWall),lh=parseFraction(leftWall);
    const rh=parseFraction(rightWall),bw=parseFraction(bottomWall);
    const tL=parseFraction(tileL),tBv=parseFraction(tileB);
    if(!isOk(tw)||!isOk(lh)||!isOk(rh)||!isOk(bw)||!isOk(tL)||!isOk(tBv)) return null;
    const topW=toInches(tw,roomUnit),leftH=toInches(lh,roomUnit);
    const rightH=toInches(rh,roomUnit),bottomW2=toInches(bw,roomUnit);
    const tileLIn=toInches(tL,tileUnit),tileBIn=toInches(tBv,tileUnit);
    const gapIn=gap==='1/8'?0.125:0.0625;
    if(!isOk(topW)||!isOk(leftH)||!isOk(rightH)||!isOk(bottomW2)||!isOk(tileLIn)||!isOk(tileBIn)) return null;
    const corners=solveCorners(topW,leftH,rightH,bottomW2);
    if(!corners) return null;
    const {TL,TR,BR,BL}=corners; const pts=[TL,TR,BR,BL]; let area=0;
    for(let i=0;i<4;i++){const[x1,y1]=pts[i],[x2,y2]=pts[(i+1)%4];area+=x1*y2-x2*y1;}
    return {topW,leftH,rightH,bottomW:bottomW2,tileLIn,tileBIn,gapIn,
      scale:computeScale(topW,leftH,rightH,bottomW2),
      areaSqFt:(Math.abs(area)/2/144).toFixed(2),
      tileAreaSqFt:((tileLIn/12)*(tileBIn/12)).toFixed(3)};
  },[topWall,leftWall,rightWall,bottomWall,tileL,tileB,gap,roomUnit,tileUnit]);

  // ── Best layout finder ─────────────────────────────────────────────────────
  // Runs all 20 combinations (4 patterns × 5 start positions) using the same
  // calcLayout function. Zero changes to any existing logic.
  const PATTERNS = [
    { value: 0,     label: 'Straight lay' },
    { value: 0.5,   label: '½ offset' },
    { value: 1/3,   label: '⅓ offset' },
    { value: 0.25,  label: '¼ offset' },
  ];
  const STARTS = [
    { value: 'left',   label: 'Left wall' },
    { value: 'right',  label: 'Right wall' },
    { value: 'top',    label: 'Top wall' },
    { value: 'bottom', label: 'Bottom wall' },
    { value: 'center', label: 'Center line' },
  ];

  const bestResult = useMemo(() => {
    if (!calc || !optimise) return null;
    const { topW, leftH, rightH, bottomW, tileLIn, tileBIn, gapIn } = calc;
    const pitchL = tileLIn + gapIn, pitchB = tileBIn + gapIn;
    const wrap = (v, p) => ((v % p) + p) % p;
    let best = null;
    for (const pat of PATTERNS) {
      for (const st of STARTS) {
        // Replicate the same shiftL/shiftB logic used in TilePreview's useMemo
        const corners0 = solveCorners(topW, leftH, rightH, bottomW);
        if (!corners0) continue;
        const maxH0 = Math.max(corners0.BL[1], corners0.BR[1]);
        let bL = 0, bB = 0;
        if      (st.value === 'right')  bL = wrap(topW, pitchL);
        else if (st.value === 'bottom') bB = wrap(maxH0, pitchB);
        else if (st.value === 'center') bL = ((topW / 2 + gapIn / 2) % pitchL + pitchL) % pitchL;
        const result = calcLayout(topW, leftH, rightH, bottomW, tileLIn, tileBIn, gapIn,
          wrap(bL, pitchL), wrap(bB, pitchB), pat.value);
        const primary   = optimise === 'cuts' ? result.totalCuts  : result.totalTiles;
        const secondary = optimise === 'cuts' ? result.totalTiles : result.totalCuts;
        if (!best ||
            primary < best.primary ||
            (primary === best.primary && secondary < best.secondary)) {
          best = { primary, secondary, offset: pat.value, startMode: st.value,
                   patLabel: pat.label, stLabel: st.label,
                   totalTiles: result.totalTiles, totalCuts: result.totalCuts };
        }
      }
    }
    return best;
  }, [calc, optimise]);

  // When best result changes, apply it to the dropdowns (unless no optimise selected)
  useEffect(() => {
    if (!bestResult) return;
    setOffset(bestResult.offset);
    setStartMode(bestResult.startMode);
    setIsCustom(false);
  }, [bestResult]);

  return (
    <>
    <Header />
    <div style={{minHeight:'100vh',background:'#f5f0e8',
      backgroundImage:'linear-gradient(#d4c8bc44 1px,transparent 1px),linear-gradient(90deg,#d4c8bc44 1px,transparent 1px)',
      backgroundSize:'36px 36px',padding:'40px 20px 80px',fontFamily:'monospace'}}>
      <div style={{maxWidth:900,margin:'0 auto'}}>
        <div style={{marginBottom:40,display:'flex',alignItems:'flex-end',gap:16}}>
          <div style={{width:52,height:52,background:'#1a1410',display:'grid',gridTemplateColumns:'1fr 1fr',gap:3,padding:8,borderRadius:5,flexShrink:0}}>
            {[0,1,2,3].map(i=><div key={i} style={{background:'#f5f0e8',borderRadius:1}}/>)}
          </div>
          <div>
            <h1 style={{fontFamily:'Georgia, serif',fontSize:'clamp(1.8rem,5vw,2.8rem)',fontStyle:'italic',letterSpacing:'-0.02em',color:'#1a1410',lineHeight:1}}>Tile Calculator</h1>
            {/* <p style={{fontSize:'0.65rem',letterSpacing:'0.18em',textTransform:'uppercase',color:'#5a4e46',marginTop:5}}>Room &amp; Tile Planner</p> */}
          </div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:20,marginBottom:20}}>
          <div style={S.card}>
            <CardLabel>Room — all 4 walls</CardLabel>
            <div style={{marginBottom:12}}>
              <label style={S.label}>Unit</label>
              <select value={roomUnit} onChange={e=>setRoomUnit(e.target.value)} style={{...S.select,marginTop:5}}>
                {UNIT_OPTIONS.map(u=><option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              <FracInput label="Top wall"    value={topWall}    onChange={setTopWall}    placeholder="e.g. 216"/>
              <FracInput label="Bottom wall" value={bottomWall} onChange={setBottomWall} placeholder="e.g. 216"/>
              <FracInput label="Left wall"   value={leftWall}   onChange={setLeftWall}   placeholder="e.g. 132"/>
              <FracInput label="Right wall"  value={rightWall}  onChange={setRightWall}  placeholder="e.g. 132"/>
            </div>
            {calc&&<div style={{marginTop:10,fontSize:'0.72rem',color:'#5a4e46',background:'#f5f0e8',padding:'7px 10px',borderRadius:4,border:'1px solid #d4c8bc'}}>Area ≈ <strong>{calc.areaSqFt} sq ft</strong></div>}
            {calc&&<RoomDiagram topW={calc.topW} leftH={calc.leftH} rightH={calc.rightH} bottomW={calc.bottomW}/>}
            {!calc&&topWall&&leftWall&&rightWall&&bottomWall&&(
              <RoomDiagram
                topW={toInches(parseFraction(topWall),roomUnit)}
                leftH={toInches(parseFraction(leftWall),roomUnit)}
                rightH={toInches(parseFraction(rightWall),roomUnit)}
                bottomW={toInches(parseFraction(bottomWall),roomUnit)}/>
            )}
          </div>
          <div style={S.card}>
            <CardLabel>Tile</CardLabel>
            <div style={{marginBottom:12}}>
              <label style={S.label}>Unit</label>
              <select value={tileUnit} onChange={e=>setTileUnit(e.target.value)} style={{...S.select,marginTop:5}}>
                {UNIT_OPTIONS.map(u=><option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <FracInput label="Length"  value={tileL} onChange={setTileL} placeholder="e.g. 15"/>
              <FracInput label="Breadth" value={tileB} onChange={setTileB} placeholder="e.g. 15"/>
            </div>
            {calc&&<div style={{marginTop:10,fontSize:'0.72rem',color:'#5a4e46',background:'#f5f0e8',padding:'7px 10px',borderRadius:4,border:'1px solid #d4c8bc'}}>Tile area: <strong>{calc.tileAreaSqFt} sq ft</strong></div>}
          </div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:20,marginBottom:20}}>
          <div style={S.card}>
            <CardLabel>Gap</CardLabel>
            <select value={gap} onChange={e=>setGap(e.target.value)} style={S.select}>
              <option value="1/16">1/16″ (1.6 mm) — Fine</option>
              <option value="1/8">1/8″ (3.2 mm) — Standard</option>
            </select>
            <div style={{marginTop:8,fontSize:'0.68rem',color:'#5a4e46'}}>{gap==='1/16'?'Rectified tiles':'Standard ceramic / porcelain'}</div>
          </div>
          <div style={S.card}>
            <CardLabel>Row Pattern</CardLabel>
            <select value={offset} onChange={e=>{setOffset(parseFloat(e.target.value));if(optimise)setIsCustom(true);}} style={S.select}>
              <option value={0}>Straight lay (0 offset)</option>
              <option value={0.5}>½ offset — Running bond</option>
              <option value={1/3}>⅓ offset — Roman bond</option>
              <option value={0.25}>¼ offset</option>
            </select>
          </div>
          <div style={S.card}>
            <CardLabel>Start Position</CardLabel>
            <select value={startMode} onChange={e=>{setStartMode(e.target.value);if(optimise)setIsCustom(true);}} style={S.select}>
              <option value="left">Left wall</option>
              <option value="right">Right wall</option>
              <option value="top">Top wall</option>
              <option value="bottom">Bottom wall</option>
              <option value="center">Center line (symmetric)</option>
            </select>
            <div style={{marginTop:8,fontSize:'0.68rem',color:'#5a4e46'}}>
              {startMode==='left'?'Full tiles from left → cut on right'
              :startMode==='right'?'Full tiles from right → cut on left'
              :startMode==='top'?'Full tiles from top → cut on bottom'
              :startMode==='bottom'?'Full tiles from bottom → cut on top'
              :'Grout line at center — equal cuts left & right'}
            </div>
          </div>
        </div>

        {/* ── Best Layout Finder ──────────────────────────────────────────── */}
        {calc && (
          <div style={{...S.card, marginBottom:20}}>
            <CardLabel>Best Layout</CardLabel>
            {/* Radio buttons — no default, user must choose */}
            <div style={{display:'flex',gap:24,marginBottom:16}}>
              {[['cuts','Min Cuts'],['tiles','Min Tiles']].map(([val,lbl])=>(
                <label key={val} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',
                  fontSize:'0.82rem',fontFamily:'monospace',color:'#1a1410',userSelect:'none'}}>
                  <input type="radio" name="optimise" value={val}
                    checked={optimise===val}
                    onChange={()=>{setOptimise(val);setIsCustom(false);}}
                    style={{accentColor:'#c0392b',width:15,height:15,cursor:'pointer'}}/>
                  {lbl}
                </label>
              ))}
              {optimise && (
                <button onClick={()=>setOptimise(null)}
                  style={{marginLeft:'auto',fontFamily:'monospace',fontSize:'0.65rem',
                    background:'none',border:'1px solid #d4c8bc',borderRadius:4,
                    padding:'3px 10px',cursor:'pointer',color:'#5a4e46'}}>
                  Clear
                </button>
              )}
            </div>

            {/* Result badge */}
            {optimise && bestResult && !isCustom && (
              <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',
                background:'#1a1410',borderRadius:6,padding:'12px 16px'}}>
                <span style={{fontSize:'0.7rem',color:'#9a8878',fontFamily:'monospace',
                  letterSpacing:'0.1em',textTransform:'uppercase'}}>Best</span>
                <span style={{fontFamily:'monospace',fontSize:'0.85rem',fontWeight:700,
                  color:'#f5f0e8'}}>{bestResult.patLabel}</span>
                <span style={{color:'#5a4e46',fontSize:'0.75rem'}}>·</span>
                <span style={{fontFamily:'monospace',fontSize:'0.85rem',fontWeight:700,
                  color:'#f5f0e8'}}>{bestResult.stLabel}</span>
                <span style={{marginLeft:'auto',display:'flex',gap:12}}>
                  <span style={{fontFamily:'monospace',fontSize:'0.78rem',
                    color: optimise==='tiles' ? '#f4a56a' : '#9a8878'}}>
                    {bestResult.totalTiles} tiles
                  </span>
                  <span style={{fontFamily:'monospace',fontSize:'0.78rem',
                    color: optimise==='cuts' ? '#f4a56a' : '#9a8878'}}>
                    {bestResult.totalCuts} cuts
                  </span>
                </span>
              </div>
            )}
            {optimise && isCustom && (
              <div style={{display:'flex',alignItems:'center',gap:10,
                background:'#f5f0e8',borderRadius:6,padding:'12px 16px',
                border:'1.5px dashed #d4c8bc'}}>
                <span style={{fontFamily:'monospace',fontSize:'0.78rem',color:'#9a8878'}}>
                  Custom layout — deviating from best
                </span>
                <button onClick={()=>{if(bestResult){setOffset(bestResult.offset);setStartMode(bestResult.startMode);setIsCustom(false);}}}
                  style={{marginLeft:'auto',fontFamily:'monospace',fontSize:'0.65rem',
                    background:'#c0392b',border:'none',borderRadius:4,
                    padding:'4px 12px',cursor:'pointer',color:'#fff'}}>
                  Restore best
                </button>
              </div>
            )}
            {!optimise && (
              <div style={{fontSize:'0.75rem',color:'#9a8878',fontFamily:'monospace'}}>
                Select a metric above to auto-find the best combination of row pattern and start position.
              </div>
            )}
          </div>
        )}

        {calc ? (
          <>
            <StatsRow layout={layout}/>
            <div style={{...S.card,marginBottom:20}}>
              <CardLabel>Layout Preview — drag to pan · hover for dimensions</CardLabel>
              <TilePreview
                topW={calc.topW} leftH={calc.leftH} rightH={calc.rightH} bottomW={calc.bottomW}
                tileL={calc.tileLIn} tileB={calc.tileBIn} gapIn={calc.gapIn}
                offset={offset} startMode={startMode} scale={calc.scale}
                onLayoutChange={onLayoutChange}/>
            </div>
            <div style={{marginBottom:20}}>
              <CutPanel layout={layout} tileL={calc.tileLIn} tileB={calc.tileBIn}/>
            </div>
            <div style={{...S.card,background:'#1a1410',border:'1.5px solid #333',boxShadow:'4px 4px 0 #333'}}>
              <div style={{fontSize:'0.6rem',letterSpacing:'0.18em',textTransform:'uppercase',color:'#9a8878',marginBottom:14}}>Summary</div>
              <div style={{color:'#c9b8a8',fontSize:'0.85rem',lineHeight:2,fontFamily:'monospace'}}>
                <div>Room: <strong style={{color:'#f5f0e8'}}>Top {fmt(calc.topW)} · Bottom {fmt(calc.bottomW)} · Left {fmt(calc.leftH)} · Right {fmt(calc.rightH)}</strong></div>
                <div>Area: <strong style={{color:'#f5f0e8'}}>≈ {calc.areaSqFt} sq ft</strong></div>
                <div>Tile: <strong style={{color:'#f5f0e8'}}>{fmt(calc.tileLIn)} × {fmt(calc.tileBIn)}</strong> · Gap: <strong style={{color:'#f5f0e8'}}>{gap}″</strong></div>
                {layout&&<div>Layout: <strong style={{color:'#f5f0e8'}}>{layout.totalTiles} tiles · {layout.totalCuts} cuts</strong></div>}
              </div>
            </div>
          </>
        ) : (
          <div style={{...S.card,textAlign:'center',padding:'48px 24px',color:'#9a8878',fontSize:'0.8rem'}}>
            Enter all 4 wall measurements and tile size to calculate
          </div>
        )}
      </div>
    </div>
    </>
  );
}